import React, { useState, useEffect } from 'react';
import { Button, Space, Typography, Tag, Divider, Empty } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { parseDataToNodes, serializeNodesToData, createEmptyNode } from './treeFieldUtils';
import { FieldNodeRow } from './FieldNodeRow';
import './TreeFieldEditor.css';

const { Text } = Typography;

const FORMAT_LABELS = {
  'plain-object': { label: '对象 {}', color: 'blue' },
  'object-array': { label: '对象数组 [{}]', color: 'green' },
  'array-with-array-values': { label: '数组含数组值 [{key:[]}]', color: 'orange' },
  'object-with-array-values': { label: '对象含数组值 {key:[]}', color: 'purple' },
  'nested-object': { label: '嵌套对象 {key:{}}', color: 'cyan' },
};

/**
 * 树状字段编辑器组件
 * 支持一级、二级、三级字段的新增和展示
 *
 * @param {unknown} data - 输入数据，支持多种格式
 * @param {function} onChange - 数据变更回调，参数为序列化后的数据对象
 * @param {boolean} [readOnly=false] - 是否只读模式
 */
const TreeFieldEditor = ({ data, onChange, readOnly = false }) => {
  const [nodes, setNodes] = useState([]);
  const [format, setFormat] = useState('plain-object');

  useEffect(() => {
    const parsed = parseDataToNodes(data);
    setNodes(parsed.nodes);
    setFormat(parsed.format);
  }, [data]);

  // 节点变更后通知外部
  const notifyChange = (newNodes) => {
    setNodes(newNodes);
    if (onChange) {
      const result = serializeNodesToData(newNodes, format);
      onChange(result);
    }
  };

  const handleUpdateNode = (index, updatedNode) => {
    const newNodes = [...nodes];
    newNodes[index] = updatedNode;
    notifyChange(newNodes);
  };

  const handleDeleteNode = (index) => {
    const newNodes = nodes.filter((_, idx) => idx !== index);
    notifyChange(newNodes);
  };

  const handleAddSibling = (afterIndex) => {
    const newNode = createEmptyNode(0);
    const newNodes = [...nodes];
    newNodes.splice(afterIndex + 1, 0, newNode);
    notifyChange(newNodes);
  };

  const handleAddRootNode = () => {
    const newNodes = [...nodes, createEmptyNode(0)];
    notifyChange(newNodes);
  };

  const formatInfo = FORMAT_LABELS[format] ?? { label: format, color: 'default' };

  return (
    <div className="tree-field-editor-container">
      <div className="tree-editor-legend">
        <Space size={4} wrap>
          <Text type="secondary" style={{ fontSize: 12 }}>图例：</Text>
          <Tag color="#1677FF" style={{ fontSize: 11 }}>一级字段</Tag>
          <Tag color="#52C41A" style={{ fontSize: 11 }}>二级字段</Tag>
          <Tag color="#FA8C16" style={{ fontSize: 11 }}>三级字段</Tag>
          <Tag color={formatInfo.color} style={{ fontSize: 11, marginLeft: 8 }}>{formatInfo.label}</Tag>
        </Space>
      </div>

      <Divider style={{ margin: '8px 0' }} />

      <div className="tree-editor-body">
        {nodes.length === 0 ? (
          <Empty description="暂无字段，点击下方按钮添加" image={Empty.PRESENTED_IMAGE_SIMPLE} />
        ) : (
          nodes.map((node, index) => (
            <FieldNodeRow
              key={node.id}
              node={node}
              onUpdate={(updated) => handleUpdateNode(index, updated)}
              onDelete={() => handleDeleteNode(index)}
              onAddSibling={() => handleAddSibling(index)}
              maxDepth={5}
            />
          ))
        )}
      </div>

      {!readOnly && (
        <>
          <Divider style={{ margin: '8px 0' }} />
          <Button
            type="dashed"
            icon={<PlusOutlined />}
            onClick={handleAddRootNode}
            block
            size="small"
          >
            添加一级字段
          </Button>
        </>
      )}
    </div>
  );
};

export { TreeFieldEditor };
